import React from 'react';
import styled from 'styled-components';
import ReactAvatar from 'react-avatar';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaBell as FaBellRaw,
  FaCheck as FaCheckRaw,
  FaRegTrashAlt as FaRegTrashAltRaw,
  FaExternalLinkAlt as FaExternalLinkAltRaw,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useBrowserPush } from '../hooks/useBrowserPush';
import {
  useClearAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
} from '../hooks/useNotifications';
import type { AppNotification } from '../types/notifications';
import { resolveNotificationDestination } from '../utils/notificationRouting';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBell: any = FaBellRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheck: any = FaCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaRegTrashAlt: any = FaRegTrashAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaExternalLinkAlt: any = FaExternalLinkAltRaw;

const Page = styled.div`
  min-height: 100%;
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeroCard = styled.section`
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 96%, transparent) 0%,
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 84%, transparent) 100%
  );
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  border-radius: 24px;
  padding: clamp(1.1rem, 2.5vw, 1.5rem);
  display: grid;
  gap: 0.8rem;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.08);
`;

const SettingsCard = styled(HeroCard)`
  gap: 0.7rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TitleWrap = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const Eyebrow = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--primary);
`;

const Title = styled.h1`
  margin: 0;
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.35rem, 2.8vw, 1.8rem);
  line-height: 1.15;
  color: var(--onSurface);
`;

const SubTitle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  line-height: 1.55;
  max-width: 56ch;
`;

const SettingsTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $tone?: 'danger' | 'secondary' | 'primary' }>`
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'danger'
        ? 'color-mix(in srgb, var(--error) 45%, transparent)'
        : $tone === 'primary'
          ? 'color-mix(in srgb, var(--primary) 35%, transparent)'
          : 'color-mix(in srgb, var(--outline) 80%, transparent)'};
  background: ${({ $tone }) =>
    $tone === 'danger'
      ? 'color-mix(in srgb, var(--error) 10%, var(--surface))'
      : $tone === 'primary'
        ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))'
        : 'color-mix(in srgb, var(--surface) 88%, var(--surfaceVariant, var(--surface)))'};
  color: ${({ $tone }) =>
    $tone === 'danger' ? 'var(--error)' : $tone === 'primary' ? 'var(--primary)' : 'var(--onSurface)'};
  border-radius: 999px;
  padding: 0.58rem 0.92rem;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SettingsText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  line-height: 1.55;
  font-size: 0.9rem;
`;

const SummaryPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 999px;
  padding: 0.45rem 0.72rem;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 72%, transparent);
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
  font-weight: 700;
`;

const EmptyState = styled.section`
  display: grid;
  place-items: center;
  gap: 0.8rem;
  border-radius: 24px;
  border: 1px dashed color-mix(in srgb, var(--outline) 78%, transparent);
  padding: 3rem 1.5rem;
  text-align: center;
  background: color-mix(in srgb, var(--surface) 90%, transparent);
  color: var(--onSurfaceVariant);
`;

const List = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const NotificationCard = styled.article<{ $unread: boolean }>`
  border-radius: 22px;
  border: 1px solid
    ${({ $unread }) =>
      $unread
        ? 'color-mix(in srgb, var(--primary) 28%, transparent)'
        : 'color-mix(in srgb, var(--outline) 82%, transparent)'};
  background: ${({ $unread }) =>
    $unread
      ? 'linear-gradient(140deg, color-mix(in srgb, var(--primary) 8%, var(--surface)), color-mix(in srgb, var(--surface) 96%, transparent))'
      : 'linear-gradient(140deg, color-mix(in srgb, var(--surface) 97%, transparent), color-mix(in srgb, var(--surfaceVariant, var(--surface)) 82%, transparent))'};
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const NotificationBody = styled.div`
  display: grid;
  gap: 1rem;
  padding: 1rem 1.1rem 1.05rem;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
`;

const AvatarWrap = styled.div`
  position: relative;
`;

const UnreadDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: var(--primary);
  border: 2px solid var(--surface);
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1;
  display: grid;
  gap: 0.4rem;
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const NotificationTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const MetaPill = styled.span<{ $tone?: 'muted' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $tone }) => ($tone === 'warning' ? 'var(--error)' : 'var(--onSurfaceVariant)')};
  background: ${({ $tone }) =>
    $tone === 'warning'
      ? 'color-mix(in srgb, var(--error) 10%, var(--surface))'
      : 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 70%, transparent)'};
`;

const SenderText = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
`;

const Message = styled.p`
  margin: 0;
  color: var(--onSurface);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FooterHint = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const ErrorBanner = styled.div`
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--error) 35%, transparent);
  background: color-mix(in srgb, var(--error) 10%, var(--surface));
  color: var(--onSurface);
  padding: 0.85rem 1rem;
  font-size: 0.9rem;
`;

const StatusText = styled.span<{ $tone?: 'error' | 'success' }>`
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--error)' : $tone === 'success' ? 'var(--primary)' : 'var(--onSurfaceVariant)'};
  font-size: 0.84rem;
  line-height: 1.5;
`;

const formatTypeLabel = (type?: string) =>
  String(type || 'notification')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatTimeAgo = (dateValue?: string) => {
  if (!dateValue) return 'Just now';

  const timestamp = new Date(dateValue).getTime();
  if (Number.isNaN(timestamp)) return 'Just now';

  const elapsedSeconds = Math.max(1, Math.floor((Date.now() - timestamp) / 1000));
  if (elapsedSeconds < 60) return 'Just now';
  if (elapsedSeconds < 3600) return `${Math.floor(elapsedSeconds / 60)}m ago`;
  if (elapsedSeconds < 86400) return `${Math.floor(elapsedSeconds / 3600)}h ago`;
  if (elapsedSeconds < 604800) return `${Math.floor(elapsedSeconds / 86400)}d ago`;
  return new Date(dateValue).toLocaleDateString();
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const browserPush = useBrowserPush(user?.id);
  const { data: notifications = [], isLoading, error } = useNotifications(user?.id);
  const markOneRead = useMarkNotificationAsRead(user?.id);
  const markAllRead = useMarkAllNotificationsAsRead(user?.id);
  const deleteNotification = useDeleteNotification(user?.id);
  const clearAllNotifications = useClearAllNotifications(user?.id);

  const unreadCount = React.useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );

  const handleOpenNotification = React.useCallback(
    async (notification: AppNotification) => {
      if (!notification.read) {
        await markOneRead.mutateAsync(notification.id);
      }
      const destination = resolveNotificationDestination(notification);
      navigate(destination.href);
    },
    [markOneRead, navigate],
  );

  const handleMarkAllRead = React.useCallback(async () => {
    if (!user?.id || unreadCount === 0) return;
    await markAllRead.mutateAsync();
  }, [markAllRead, unreadCount, user?.id]);

  const handleClearAll = React.useCallback(async () => {
    if (!user?.id || notifications.length === 0) return;
    const confirmed = window.confirm('Clear all notifications? This will remove them from your inbox.');
    if (!confirmed) return;
    await clearAllNotifications.mutateAsync();
  }, [clearAllNotifications, notifications.length, user?.id]);

  const browserPushMessage = React.useMemo(() => {
    if (browserPush.loading) {
      return 'Checking browser notification support...';
    }
    if (!browserPush.status.supported) {
      return 'This browser does not support service-worker push notifications.';
    }
    if (!browserPush.status.secureContext) {
      return 'Browser push requires HTTPS, or localhost while developing.';
    }
    if (!browserPush.status.configured) {
      return 'Web push is not configured yet. Add the public VAPID key to enable subscriptions.';
    }
    if (browserPush.status.permission === 'denied') {
      return 'Browser notifications are blocked for this site. Re-enable them in your browser settings to subscribe again.';
    }
    if (browserPush.status.subscribed) {
      return 'This browser is subscribed and can receive notifications even when the dashboard is closed.';
    }
    return 'Enable browser notifications to receive alerts from the dashboard even when this tab is closed.';
  }, [browserPush.loading, browserPush.status]);

  return (
    <Page>
      <HeroCard>
        <TitleRow>
          <TitleWrap>
            <Eyebrow>Notifications</Eyebrow>
            <Title>Stay on top of alerts and follow-ups</Title>
            <SubTitle>
              Open messages, approvals, community activity, and reminders from one place. Each notification can
              take you straight to the relevant page when a web destination is available.
            </SubTitle>
          </TitleWrap>
          <Controls>
            <Button
              type="button"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0 || markAllRead.isPending}
              $tone="primary"
            >
              <FaCheck />
              {markAllRead.isPending ? 'Marking...' : 'Mark All Read'}
            </Button>
            <Button
              type="button"
              onClick={handleClearAll}
              disabled={notifications.length === 0 || clearAllNotifications.isPending}
              $tone="danger"
            >
              <FaRegTrashAlt />
              {clearAllNotifications.isPending ? 'Clearing...' : 'Clear All'}
            </Button>
          </Controls>
        </TitleRow>
        <SummaryRow>
          <SummaryPill>
            <FaBell />
            {notifications.length} total
          </SummaryPill>
          <SummaryPill>{unreadCount} unread</SummaryPill>
        </SummaryRow>
      </HeroCard>

      <SettingsCard>
        <TitleWrap>
          <Eyebrow>Browser Push</Eyebrow>
          <SettingsTitle>Desktop notifications for this browser</SettingsTitle>
          <SettingsText>{browserPushMessage}</SettingsText>
        </TitleWrap>
        <SummaryRow>
          <SummaryPill>Permission: {browserPush.status.permission}</SummaryPill>
          <SummaryPill>{browserPush.status.subscribed ? 'Subscribed' : 'Not subscribed'}</SummaryPill>
        </SummaryRow>
        {browserPush.error ? <StatusText $tone="error">{browserPush.error}</StatusText> : null}
        {!browserPush.error && browserPush.status.subscribed ? (
          <StatusText $tone="success">Browser push is active for this device.</StatusText>
        ) : null}
        <Controls>
          <Button
            type="button"
            onClick={() => browserPush.subscribe()}
            disabled={
              browserPush.updating ||
              browserPush.loading ||
              !browserPush.status.supported ||
              !browserPush.status.secureContext ||
              !browserPush.status.configured ||
              browserPush.status.permission === 'denied' ||
              browserPush.status.subscribed
            }
            $tone="primary"
          >
            <FaBell />
            {browserPush.updating && !browserPush.status.subscribed ? 'Enabling...' : 'Enable Browser Push'}
          </Button>
          <Button
            type="button"
            onClick={() => browserPush.unsubscribe()}
            disabled={browserPush.updating || browserPush.loading || !browserPush.status.subscribed}
          >
            <FaRegTrashAlt />
            {browserPush.updating && browserPush.status.subscribed ? 'Removing...' : 'Disable Browser Push'}
          </Button>
          <Button type="button" onClick={() => browserPush.refresh()} disabled={browserPush.updating}>
            Refresh Status
          </Button>
        </Controls>
      </SettingsCard>

      {error ? <ErrorBanner>{error instanceof Error ? error.message : 'Failed to load notifications.'}</ErrorBanner> : null}

      {isLoading ? (
        <EmptyState>
          <FaBell size={28} />
          <div>Loading notifications...</div>
        </EmptyState>
      ) : notifications.length === 0 ? (
        <EmptyState>
          <FaBell size={28} />
          <div>No notifications yet.</div>
          <FooterHint>When people interact with your account or content, they will appear here.</FooterHint>
        </EmptyState>
      ) : (
        <List>
          {notifications.map((notification) => {
            const destination = resolveNotificationDestination(notification);
            const senderName =
              notification.sender?.full_name ||
              notification.sender_name ||
              (typeof notification.data?.senderName === 'string' ? notification.data.senderName : '');

            return (
              <NotificationCard key={notification.id} $unread={!notification.read}>
                <NotificationBody>
                  <NotificationHeader>
                    <AvatarWrap>
                      <ReactAvatar
                        name={senderName || notification.title || 'Notification'}
                        src={notification.sender?.avatar || undefined}
                        size="44"
                        round
                      />
                      {!notification.read ? <UnreadDot /> : null}
                    </AvatarWrap>
                    <HeaderText>
                      <NotificationTitle>{notification.title || 'Notification'}</NotificationTitle>
                      <HeaderMeta>
                        <MetaPill>{formatTypeLabel(notification.type)}</MetaPill>
                        <MetaPill>{formatTimeAgo(notification.created_at)}</MetaPill>
                        {!destination.supported ? <MetaPill $tone="warning">Limited web support</MetaPill> : null}
                      </HeaderMeta>
                      {senderName ? <SenderText>From {senderName}</SenderText> : null}
                    </HeaderText>
                  </NotificationHeader>

                  <Message>{notification.message}</Message>

                  <FooterRow>
                    <FooterHint>
                      {destination.supported
                        ? 'This notification can open a matching page on the web dashboard.'
                        : 'This notification is visible on web, but its exact mobile destination is not available here yet.'}
                    </FooterHint>
                    <ActionRow>
                      {!notification.read ? (
                        <Button
                          type="button"
                          onClick={() => markOneRead.mutate(notification.id)}
                          disabled={markOneRead.isPending}
                        >
                          <FaCheck />
                          Mark Read
                        </Button>
                      ) : null}
                      <Button type="button" onClick={() => handleOpenNotification(notification)} $tone="primary">
                        <FaExternalLinkAlt />
                        {destination.label}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => deleteNotification.mutate(notification)}
                        disabled={deleteNotification.isPending}
                        $tone="danger"
                      >
                        <FaRegTrashAlt />
                        Delete
                      </Button>
                    </ActionRow>
                  </FooterRow>
                </NotificationBody>
              </NotificationCard>
            );
          })}
        </List>
      )}
    </Page>
  );
}
